import * as React from "react";
import {
    ColumnDef,
    ColumnFiltersState,
    SortingState,
    VisibilityState,
    flexRender,
    getCoreRowModel,
    getFilteredRowModel,
    getPaginationRowModel,
    getSortedRowModel,
    useReactTable,
    Row,
} from "@tanstack/react-table";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";
import { useEffect } from "react";

export type Payment = {
    id: string;
    amount: number;
    status: "pending" | "processing" | "success" | "failed";
    email: string;
};

interface CardsDataTableProps<T> {
    title: string;
    description: string;
    columns: ColumnDef<T>[];
    data: T[];
    resourceKey?: string;
    onRowClick?: (event: React.MouseEvent<HTMLTableRowElement>, row: T) => void;
    onSelectionChange?: (rows: T[]) => void;
    // Pagination props
    totalCount?: number;
    currentPage?: number;
    pageSize?: number;
    onPageChange?: (page: number) => void;
    // Loading state
    isLoading?: boolean;

    /** Optional actions to display in the table header */
    actions?: React.ReactNode;
}

export function CardsDataTable<T>({
    title,
    description,
    columns,
    resourceKey,
    data,
    onRowClick,
    onSelectionChange,
    totalCount = 0,
    currentPage = 0,
    pageSize = 10,
    onPageChange,
    isLoading,
    actions,
}: CardsDataTableProps<T>) {
    const { t } = useTranslation();
    const [sorting, setSorting] = React.useState<SortingState>([]);
    const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>([]);
    const [columnVisibility, setColumnVisibility] = React.useState<VisibilityState>({});
    const [rowSelection, setRowSelection] = React.useState({});

    // Reset row selection when data changes
    useEffect(() => {
        setRowSelection({});
    }, [data]);

    const table = useReactTable({
        data,
        columns,
        onSortingChange: setSorting,
        onColumnFiltersChange: setColumnFilters,
        getCoreRowModel: getCoreRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        onColumnVisibilityChange: setColumnVisibility,
        onRowSelectionChange: setRowSelection,
        state: {
            sorting,
            columnFilters,
            columnVisibility,
            rowSelection,
            pagination: {
                pageIndex: currentPage,
                pageSize: pageSize,
            },
        },
        manualPagination: true,
        pageCount: Math.ceil(totalCount / pageSize),
        // Add enableMultiRowSelection option to control multiple selection behavior
        enableMultiRowSelection: true,
    });

    const handleRowClick = (event: React.MouseEvent<HTMLTableRowElement>, row: Row<T>) => {
        if (onRowClick) {
            onRowClick(event, row.original);
        }
    };

    return (
        <Card>
            <CardHeader className="flex flex-row justify-between">
                <div className="flex flex-col gap-1">
                    <CardTitle className="text-xl">{title}</CardTitle>
                    <CardDescription>{description}</CardDescription>
                </div>
                {actions && <div className="flex items-center gap-2">{actions}</div>}
            </CardHeader>
            <CardContent>
                <div className="mb-4 flex items-center gap-4">
                    <Input
                        placeholder={
                            resourceKey
                                ? t(`screens.${resourceKey}.searchPlaceholder`)
                                : t("general.searchPlaceholder")
                        }
                        className="max-w-sm"
                    />
                </div>
                <div className="rounded-md border">
                    <Table>
                        <TableHeader>
                            {table.getHeaderGroups().map((headerGroup) => (
                                <TableRow key={headerGroup.id}>
                                    {headerGroup.headers.map((header) => {
                                        return (
                                            <TableHead
                                                key={header.id}
                                                className="[&:has([role=checkbox])]:pl-3"
                                            >
                                                {header.isPlaceholder
                                                    ? null
                                                    : flexRender(
                                                          header.column.columnDef.header,
                                                          header.getContext(),
                                                      )}
                                            </TableHead>
                                        );
                                    })}
                                </TableRow>
                            ))}
                        </TableHeader>
                        <TableBody>
                            {table.getRowModel().rows?.length ? (
                                table.getRowModel().rows.map((row) => (
                                    <TableRow
                                        key={row.id}
                                        data-state={row.getIsSelected() && "selected"}
                                        onClick={(event) => handleRowClick(event, row)}
                                        className={cn(
                                            onRowClick && "cursor-pointer hover:bg-muted/50",
                                        )}
                                    >
                                        {row.getVisibleCells().map((cell) => (
                                            <TableCell
                                                key={cell.id}
                                                className="[&:has([role=checkbox])]:pl-3"
                                            >
                                                {flexRender(
                                                    cell.column.columnDef.cell,
                                                    cell.getContext(),
                                                )}
                                            </TableCell>
                                        ))}
                                    </TableRow>
                                ))
                            ) : (
                                <TableRow>
                                    <TableCell
                                        colSpan={columns.length}
                                        className="h-24 text-center"
                                    >
                                        No results.
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </div>
                <div className="flex items-center justify-end space-x-2 pt-4">
                    <div className="flex-1 text-sm text-muted-foreground">
                        {t("general.table.rowsSelected", {
                            count: table.getFilteredSelectedRowModel().rows.length,
                        })}
                    </div>
                    <div className="space-x-2">
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => onPageChange?.(currentPage - 1)}
                            disabled={currentPage === 0}
                        >
                            {t("general.previous")}
                        </Button>
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => onPageChange?.(currentPage + 1)}
                            disabled={currentPage >= Math.ceil(totalCount / pageSize) - 1}
                        >
                            {t("general.next")}
                        </Button>
                    </div>
                </div>
            </CardContent>
        </Card>
    );
}
